const BUCKET = 'walllabs-bucket'
const REGION = 'us-east-1'

/** Extensions allowed for wallpaper uploads (incl. common aliases like .peg → jpg). */
export const ALLOWED_IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'jpe',
  'peg',
  'jfif',
  'png',
  'gif',
  'webp',
  'svg',
  'bmp',
  'ico',
  'avif',
  'heic',
  'heif',
  'tif',
  'tiff',
  'json'
]

const EXTENSION_FROM_FILENAME =
  /\.(jpe?g|jfif|png|gif|webp|svg|bmp|ico|avif|heic|heif|tiff?|json|peg)$/i

const MIME_TO_EXTENSION = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/pjpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
  'image/bmp': 'bmp',
  'image/x-icon': 'ico',
  'image/vnd.microsoft.icon': 'ico',
  'image/avif': 'avif',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/tiff': 'tif',
  'application/json': 'json'
}

/** File picker accept list for v-file-input. */
export const IMAGE_FILE_ACCEPT = [
  'image/*',
  ...ALLOWED_IMAGE_EXTENSIONS.map((ext) => `.${ext}`)
].join(',')

function normalizeExtension(ext) {
  const lower = String(ext || '').toLowerCase()
  if (lower === 'jpeg' || lower === 'jpe' || lower === 'peg') return 'jpg'
  if (lower === 'tiff') return 'tif'
  return lower
}

export function getExtension(fileName = '', mimeType = '') {
  const fromName = String(fileName).match(EXTENSION_FROM_FILENAME)?.[1]
  if (fromName) {
    return normalizeExtension(fromName)
  }

  const fromMime = MIME_TO_EXTENSION[String(mimeType || '').toLowerCase()]
  if (fromMime) return fromMime

  const mimeSub = String(mimeType || '')
    .toLowerCase()
    .replace(/^image\//, '')
    .split(';')[0]
    .trim()
  if (mimeSub && ALLOWED_IMAGE_EXTENSIONS.includes(normalizeExtension(mimeSub))) {
    return normalizeExtension(mimeSub)
  }

  return 'png'
}

export function isAllowedImageFile(file) {
  if (!file) return false

  const name = String(file.name || '')
  const type = String(file.type || '').toLowerCase()
  const ext = getExtension(name, type)

  if (ALLOWED_IMAGE_EXTENSIONS.includes(ext)) {
    return true
  }

  return type.startsWith('image/')
}

export function resolveUploadContentType(file, extension) {
  const ext = normalizeExtension(extension)
  const byExt = {
    jpg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    bmp: 'image/bmp',
    ico: 'image/x-icon',
    avif: 'image/avif',
    heic: 'image/heic',
    heif: 'image/heif',
    tif: 'image/tiff',
    json: 'application/json'
  }

  if (file?.type && isAllowedImageFile(file)) {
    return file.type
  }

  return byExt[ext] || 'application/octet-stream'
}

function sanitizeNameSegment(name = '') {
  const normalized = String(name || '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[\\/:*?"<>|#%{}^~[\]`]/g, '')
  return normalized || 'wallpaper'
}

function encodeKeyForUrl(key) {
  return String(key || '')
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/')
}

export function buildS3Key(wallpaperName, file) {
  const base = sanitizeNameSegment(wallpaperName)
  const extension = getExtension(file?.name, file?.type)
  return `wallpaper/${base}.${extension}`
}

export function getPublicS3Url(key) {
  const cleaned = String(key || '').replace(/^\/+/, '')
  return `https://${BUCKET}.s3.${REGION}.amazonaws.com/${encodeKeyForUrl(cleaned)}`
}

function extractS3KeyFromUrl(url) {
  try {
    const parsed = new URL(String(url || '').trim())
    const host = parsed.hostname.toLowerCase()
    if (
      !host.includes('walllabs-bucket.s3.amazonaws.com') &&
      !host.includes('walllabs-bucket.s3.us-east-1.amazonaws.com')
    ) {
      return ''
    }
    return decodeURIComponent(parsed.pathname.replace(/^\/+/, ''))
  } catch {
    return ''
  }
}

/** Rebuild a consistent public S3 URL (fixes mixed hosts / encoding from API or upload). */
/** True when the catalog should use a shared remote URL (S3/API), not a device-only blob. */
export function isPublicRemoteImageUrl(url) {
  const value = String(url || '').trim()
  return /^https?:\/\//i.test(value) && !value.startsWith('blob:')
}

export function normalizeS3PublicUrl(url) {
  const raw = String(url || '').trim()
  if (!raw) return ''

  const key = extractS3KeyFromUrl(raw)
  if (key) {
    return getPublicS3Url(key)
  }

  return raw
}

/** Dev: proxy via /s3-image. Production: direct canonical S3 URL. */
export function getDisplayImageUrl(imageUrl) {
  const url = normalizeS3PublicUrl(imageUrl) || String(imageUrl || '').trim()
  if (!url) return ''

  try {
    const parsed = new URL(url)
    if (
      parsed.hostname.includes('walllabs-bucket.s3.amazonaws.com') ||
      parsed.hostname.includes('walllabs-bucket.s3.us-east-1.amazonaws.com')
    ) {
      if (process.env.NODE_ENV === 'development') {
        return `/s3-image${parsed.pathname}${parsed.search}`
      }
    }
  } catch {
    return url
  }

  return url
}

function isHtmlUploadResponse(text) {
  const sample = String(text || '').trim().slice(0, 200).toLowerCase()
  return (
    sample.startsWith('<!doctype') ||
    sample.startsWith('<html') ||
    sample.includes('without javascript enabled')
  )
}

function deriveUploadApiBase() {
  const explicit = String(process.env.VUE_APP_UPLOAD_API_BASE || '').trim()
  if (explicit) return explicit.replace(/\/$/, '')

  const listUrl = String(
    process.env.VUE_APP_WALLLABS_API_URL ||
      'https://pt8lbf5drh.execute-api.us-east-1.amazonaws.com/dev/walllabs_create'
  ).trim()

  if (!listUrl) return ''

  return listUrl.replace(/\/walllabs_create\/?$/i, '/upload-s3')
}

function getUploadUrlCandidates(encodedKey) {
  const candidates = []
  const uploadBase = deriveUploadApiBase()
  const isDev = process.env.NODE_ENV === 'development'

  if (uploadBase) {
    if (uploadBase.includes('{key}')) {
      candidates.push(uploadBase.replace('{key}', encodedKey))
    } else {
      candidates.push(`${uploadBase}/${encodedKey}`)
    }
  }

  if (isDev) {
    candidates.push(`/upload-s3/${encodedKey}`)
    candidates.push(`/api/upload-s3/${encodedKey}`)
  }

  return [...new Set(candidates)]
}

async function uploadViaProxy(file, key) {
  const encodedKey = encodeKeyForUrl(key)
  const extension = getExtension(file?.name, file?.type)
  const contentType = resolveUploadContentType(file, extension)
  const proxyUrls = getUploadUrlCandidates(encodedKey)

  let lastError = null

  for (const proxyUrl of proxyUrls) {
    try {
      const response = await fetch(proxyUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': contentType,
          'x-amz-acl': 'public-read'
        },
        body: file
      })

      const text = await response.text()

      if (isHtmlUploadResponse(text)) {
        lastError = new Error(
          'Upload API not reachable (got HTML). Deploy Lambda upload-s3 on API Gateway — see aws/UPLOAD_API_SETUP.md'
        )
        continue
      }

      let data = {}
      try {
        data = text ? JSON.parse(text) : {}
      } catch {
        data = {}
      }

      if (response.ok) {
        return data?.url || getPublicS3Url(key)
      }

      lastError = new Error(
        data?.message || `Upload failed (${response.status})`
      )
    } catch (error) {
      lastError = error
    }
  }

  throw (
    lastError ||
    new Error(
      'Upload failed. Deploy upload API (aws/UPLOAD_API_SETUP.md) or set VUE_APP_UPLOAD_API_BASE and rebuild.'
    )
  )
}

async function tryCatalogApiUpload(file, key) {
  const { uploadWallpaperViaCatalogApi } = await import('./wallLabsApi')
  return uploadWallpaperViaCatalogApi(file, key)
}

/** Upload file to S3 first; callers should invoke the wallpaper API only after this resolves. */
export async function uploadToS3(file, key) {
  if (!file) throw new Error('No file selected')
  if (!key) throw new Error('No S3 key provided')
  if (!isAllowedImageFile(file)) {
    throw new Error(
      `Unsupported file type. Allowed: ${ALLOWED_IMAGE_EXTENSIONS.join(', ')}`
    )
  }

  if (process.env.NODE_ENV === 'production') {
    try {
      const catalogUrl = await tryCatalogApiUpload(file, key)
      return normalizeS3PublicUrl(catalogUrl) || catalogUrl
    } catch {
      // Backend may not implement uploadWallpaper — use PUT upload API next.
    }
  }

  const url = await uploadViaProxy(file, key)
  if (!url) {
    throw new Error('S3 upload completed without a public URL.')
  }

  return url
}
